import React, { useState, useEffect } from "react";
import { X, Check, Loader2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

interface Props {
  user: any;
  onClose: () => void;
  onSuccess: () => void;
}

const ApprovalModal: React.FC<Props> = ({ user, onClose, onSuccess }) => {
  const { role: myRole, token } = useAdminAuth();
  
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedTL, setSelectedTL] = useState("");
  
  const [admins, setAdmins] = useState([]);
  const [managers, setManagers] = useState([]);
  const [tls, setTls] = useState([]);
  const [loading, setLoading] = useState(false);

  const allowedRoles = myRole === "SUPER_ADMIN" 
    ? ["ADMIN", "MANAGER", "TEAM_LEADER", "AGENT"]
    : ["MANAGER", "TEAM_LEADER", "AGENT"];

  const fetchHierarchy = async (roleType: string, parentId: string | null, setState: any) => {
    let url = `/springboot/api/admin/team/hierarchy?role=${roleType}`;
    if (parentId) url += `&parentId=${parentId}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setState(await res.json());
  };

  useEffect(() => {
    if (myRole === "SUPER_ADMIN" && ["MANAGER", "TEAM_LEADER", "AGENT"].includes(selectedRole)) {
      fetchHierarchy("ADMIN", null, setAdmins);
    }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole === "TEAM_LEADER" || selectedRole === "AGENT") {
      if (myRole === "SUPER_ADMIN" && selectedAdmin) {
        fetchHierarchy("MANAGER", selectedAdmin, setManagers);
      } else if (myRole === "ADMIN") {
        fetchHierarchy("MANAGER", null, setManagers);
      }
    }
  }, [selectedAdmin, selectedRole]);

  useEffect(() => {
    if (selectedRole === "AGENT" && selectedManager) {
      fetchHierarchy("TEAM_LEADER", selectedManager, setTls);
    }
  }, [selectedManager, selectedRole]);

  const handleApprove = async () => {
    let finalParentId = null;
    if (selectedRole === "ADMIN") finalParentId = null;
    else if (selectedRole === "MANAGER") finalParentId = selectedAdmin ? Number(selectedAdmin) : null;
    else if (selectedRole === "TEAM_LEADER") finalParentId = selectedManager ? Number(selectedManager) : null;
    else if (selectedRole === "AGENT") finalParentId = selectedTL ? Number(selectedTL) : null;

    const payload = { userId: user.id, role: selectedRole, explicitParentId: finalParentId };

    try {
        setLoading(true);
        const res = await fetch("/springboot/api/admin/team/approve", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload)
        });
        
        if(res.ok) { onSuccess(); onClose(); } 
        else { const err = await res.text(); alert("Error: " + err); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-[100] backdrop-blur-md p-4 font-sans">
      <div className="bg-white w-full max-w-lg rounded-[32px] border border-slate-100 p-10 shadow-[0_20px_60px_rgba(0,0,0,0.1)] relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-50 rounded-full"><X size={20}/></button>
        
        <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0F172A] tracking-tight">Approve Access</h2>
            <p className="text-slate-500 text-sm mt-1">Setup profile for <span className="text-emerald-600 font-extrabold">{user.name}</span></p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 ml-1">Assign Organization Role</label>
            <select 
              className="w-full mt-2 bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600 outline-none transition-all shadow-sm"
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setSelectedAdmin(""); setSelectedManager(""); setSelectedTL("");
              }}
            >
              <option value="">-- Select Role --</option>
              {allowedRoles.map(r => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>

          {(myRole === "SUPER_ADMIN" && ["MANAGER", "TEAM_LEADER", "AGENT"].includes(selectedRole)) && (
             <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 ml-1">Parent Admin</label>
                <select 
                    className="w-full mt-2 bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-2xl focus:border-emerald-600 outline-none transition-all shadow-sm"
                    onChange={(e) => setSelectedAdmin(e.target.value)}
                >
                    <option value="">-- Select Admin --</option>
                    {admins.map((a:any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
             </div>
          )}

          {(selectedRole === "TEAM_LEADER" || selectedRole === "AGENT") && (
             <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 ml-1">Reporting Manager</label>
                <select 
                    className="w-full mt-2 bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-2xl focus:border-emerald-600 outline-none transition-all shadow-sm"
                    onChange={(e) => setSelectedManager(e.target.value)}
                    disabled={myRole === "SUPER_ADMIN" && !selectedAdmin}
                >
                    <option value="">-- Select Manager --</option>
                    {managers.map((m:any) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
             </div>
          )}

          {selectedRole === "AGENT" && (
             <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 ml-1">Team Leader</label>
                <select 
                    className="w-full mt-2 bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-2xl focus:border-emerald-600 outline-none transition-all shadow-sm"
                    onChange={(e) => setSelectedTL(e.target.value)}
                    disabled={!selectedManager}
                >
                    <option value="">-- Select Team Leader --</option>
                    {tls.map((t:any) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
             </div>
          )}
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-slate-100 pt-8">
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xs uppercase tracking-widest font-extrabold transition-colors">Dismiss</button>
            <button 
                onClick={handleApprove}
                disabled={loading || !selectedRole}
                className="bg-[#059669] hover:bg-[#047857] text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs flex items-center gap-3 shadow-lg shadow-emerald-100 transition-all active:scale-95 disabled:opacity-50"
            >
                {loading ? <Loader2 className="animate-spin" size={16}/> : "Confirm Access"} <Check size={18}/>
            </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;